import {
  toWSDateString,
  useModalOldContext,
  WSElement,
  WSGrid,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { MemberWorkFlowStatus } from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import { useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { useUserId } from "../../../query/hooks/helpers";
import {
  useQueryPayrollSettings,
  useQueryPayables
} from "../../../query/payments/queries";
import { WSQueries } from "../../../query/WSQuery";
import { useDefaultCurrency } from "../../../shared/hooks/useDefaultCurrency";
import { BulkUnapproveModal } from "../../components/NewPayablesTable/BulkUnapproveModal";
import {
  PayablesStatusFilter,
  PayablesTable,
  usePayablesQuery
} from "../../components/NewPayablesTable/PayablesTable";
import { PayableActionBlock } from "../../components/PayableActionBlock/PayableActionBlock";
import { PayablesSummary } from "../../components/PayablesSummary/PayablesSummary";
import { Summary } from "../../components/Summary/Summary";
import { DateRangeFilter } from "../../utils";
import { getNextPayrollCutOffDate } from "../../utils/payables";
import {
  API_QUERY_PAYABLES_OPEN,
  API_QUERY_PAYABLES_OVERDUE,
  API_QUERY_PAYABLES_TO_APPROVE
} from "../../utils/payables/apiQueries";
import { PAYROLL_SETTINGS_MODAL } from "./PayrollSettingsModal";

export const PayablesDashboard: React.FC = () => {
  useBrowserPageTitle("Payables Dashboard");
  const userId = useUserId();
  const history = useHistory();

  const { openModal } = useModalOldContext();

  const queryPayablesScheduled = usePayablesQuery({
    status: [PayablesStatusFilter.Scheduled]
  });

  const queryPayablesToApprove = useQueryPayables({
    ...API_QUERY_PAYABLES_TO_APPROVE,
    page: {
      size: 1
    }
  });

  const queryPayablesDisputed = usePayablesQuery({
    status: [PayablesStatusFilter.ToApprove, PayablesStatusFilter.Scheduled],
    custom: {
      "member.workflowStatus": MemberWorkFlowStatus.Disputed
    }
  });

  const queryPayablesOpen = useQueryPayables({
    ...API_QUERY_PAYABLES_OPEN,
    page: {
      size: 1
    }
  });

  const queryPayablesOverdue = useQueryPayables({
    ...API_QUERY_PAYABLES_OVERDUE,
    page: {
      size: 1
    }
  });

  const queryPayrollSettings = useQueryPayrollSettings(userId, {
    retry: false
  });

  const defaultCurrency = useDefaultCurrency();

  return (
    <>
      <WSElement mb="2XL">
        <WSElement mb="2XL">
          <WSGrid>
            <WSGrid.Item span={{ xs: "6", s: "4", m: "3" }}>
              <Summary
                label="Open"
                color="amber300"
                value={
                  queryPayablesOpen.data?.summary.totalValue !== undefined
                    ? formatMoney(
                        queryPayablesOpen.data.summary.totalValue,
                        defaultCurrency
                      )
                    : "–"
                }
              />
            </WSGrid.Item>
            <WSGrid.Item span={{ xs: "6", s: "4", m: "3" }}>
              <Summary
                label="Overdue"
                color="red300"
                value={
                  queryPayablesOverdue.data?.summary.totalValue !== undefined
                    ? formatMoney(
                        queryPayablesOverdue.data.summary.totalValue,
                        defaultCurrency
                      )
                    : "–"
                }
              />
            </WSGrid.Item>
            <WSGrid.Item span={{ xs: "8", s: "4", m: "3" }}>
              <PayablesSummary
                label="Paid (last 30 days)"
                filter={{
                  status: [PayablesStatusFilter.Paid],
                  paidAtRange: DateRangeFilter.PastMonth
                }}
                color="green300"
                displayAmount
              />
            </WSGrid.Item>
          </WSGrid>
        </WSElement>

        <WSQueries
          queries={{
            queryPayablesScheduled,
            queryPayablesToApprove,
            queryPayablesDisputed,
            queryPayrollSettings
          }}
          renderErrors={() => null}
        >
          {({
            queryPayablesScheduled: {
              data: { summary: scheduledPayablesSummary }
            },
            queryPayablesToApprove: {
              data: {
                data: payablesToApprove,
                summary: toApprovePayablesSummary
              }
            },
            queryPayablesDisputed: {
              data: { summary: disputedPayablesSummary }
            },
            queryPayrollSettings: { data: payrollSettings }
          }) => (
            <>
              <WSElement mb="3XL">
                {scheduledPayablesSummary.listSize > 0 && (
                  <PayableActionBlock
                    mb="M"
                    icon="calendar"
                    onClick={() => openModal(PAYROLL_SETTINGS_MODAL)}
                    contentButton={{
                      children: "Manage payroll settings"
                    }}
                  >
                    <WSText.ParagraphSm>
                      <WSText.ParagraphSm as="b" inline>
                        Next payroll is{" "}
                        {toWSDateString(
                          getNextPayrollCutOffDate(payrollSettings),
                          "monthDate"
                        )}
                      </WSText.ParagraphSm>
                      . {scheduledPayablesSummary.listSize} payable
                      {scheduledPayablesSummary.listSize > 1 ? "s" : ""} with a
                      total of{" "}
                      {formatMoney(
                        scheduledPayablesSummary.totalValue,
                        defaultCurrency
                      )}{" "}
                      across future payrolls.
                    </WSText.ParagraphSm>
                  </PayableActionBlock>
                )}

                {toApprovePayablesSummary.listSize > 0 && (
                  <PayableActionBlock
                    mb="M"
                    icon="dollar-circle"
                    contentButton={{
                      children: "Review & bulk approve"
                    }}
                    onClick={() => {
                      history.push("/member/invoices/payables/to-approve");
                    }}
                  >
                    <WSText.ParagraphSm color="gray500" mb="XS">
                      <WSText.ParagraphSm as="b" inline color="gray700">
                        Payments to approve!
                      </WSText.ParagraphSm>{" "}
                      {toApprovePayablesSummary.listSize} payable
                      {toApprovePayablesSummary.listSize > 1 ? "s" : ""}{" "}
                      totaling{" "}
                      {formatMoney(
                        toApprovePayablesSummary.totalValue,
                        defaultCurrency
                      )}
                      .
                    </WSText.ParagraphSm>
                  </PayableActionBlock>
                )}

                {disputedPayablesSummary.listSize > 0 && (
                  <PayableActionBlock
                    onClick={() => {
                      history.push("/member/invoices/payables/disputed");
                    }}
                    icon="alert-circle"
                    color="amber400"
                    colorBackground="amber50"
                    contentButton={{
                      children: "Manage disputed payables"
                    }}
                  >
                    <WSText.ParagraphSm color="gray500">
                      <WSText.ParagraphSm as="b" color="gray700">
                        You have {disputedPayablesSummary.listSize} disputed
                        payable
                        {disputedPayablesSummary.listSize > 1 ? "s" : ""} that
                        need your attention.
                      </WSText.ParagraphSm>
                    </WSText.ParagraphSm>
                  </PayableActionBlock>
                )}
              </WSElement>
            </>
          )}
        </WSQueries>
      </WSElement>

      <BulkUnapproveModal />

      <PayablesTable
        showHeaders
        title="Recent payable activity"
        basePath="/member/invoices/payables/dashboard"
        pagination={{ limit: 5, disableInfiniteScroll: true }}
        defaultFilters={{
          sort: { updatedAt: "desc" }
        }}
        emptyState={{
          firstTime: "New and recently changed payables will display here.",
          standart:
            "Nothing to see here! New and recently changed payables display here."
        }}
      />
    </>
  );
};
